"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  type LocaleOption,
} from "@/lib/constants/locale-data";

type LocaleContextType = {
  language: string;
  country: string;
  languageOption: LocaleOption | undefined;
  countryOption: LocaleOption | undefined;
  setLanguage: (language: string) => void;
  setCountry: (country: string) => void;
  languageOptions: LocaleOption[];
  countryOptions: LocaleOption[];
  isLoaded: boolean;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

interface LocaleProviderProps {
  children: ReactNode;
  initialLanguage?: string;
  initialCountry?: string;
}

export function LocaleProvider({
  children,
  initialLanguage = "en",
  initialCountry = "US",
}: LocaleProviderProps) {
  const [language, setLanguageState] = useState<string>(initialLanguage);
  const [country, setCountryState] = useState<string>(initialCountry);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load saved preferences from localStorage if available
    const savedLanguage = localStorage.getItem("userLanguage");
    const savedCountry = localStorage.getItem("userCountry");

    if (savedLanguage) {
      setLanguageState(savedLanguage);
    }

    if (savedCountry) {
      setCountryState(savedCountry);
    }

    setIsLoaded(true);
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (!isLoaded) return;
    localStorage.setItem("userLanguage", language);
    localStorage.setItem("userCountry", country);
  }, [language, country, isLoaded]);

  const setLanguage = (newLanguage: string) => {
    setLanguageState(newLanguage);
  };

  const setCountry = (newCountry: string) => {
    setCountryState(newCountry);
  };

  // Find the full option objects based on the selected values
  const languageOption = LANGUAGE_OPTIONS.find(
    (option) => option.value === language
  );
  const countryOption = COUNTRY_OPTIONS.find(
    (option) => option.value === country
  );

  const value = {
    language,
    country,
    languageOption,
    countryOption,
    setLanguage,
    setCountry,
    languageOptions: LANGUAGE_OPTIONS,
    countryOptions: COUNTRY_OPTIONS,
    isLoaded,
  };

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}
